<template>
  <div>
    <v-row class="mx-2">
      <v-col cols="12">
        <app-title texto="FORMAÇÃO ACADÊMICA" />
      </v-col>

      <!-- Gráfico de pizza - níveis de escolaridades -->
      <v-col cols="12">
        <app-title texto="ESCOLARIDADE" cor="secondary" />
      </v-col>
      <v-col cols="12">
        <pie-chart
          titulo="Comparativo dos Níveis de Escolaridades"
          :fonte="`Sistema Desenvolva, ${ano}`"
          :dados="dadosPieChartNiveisEscolaridade.series"
          :is-get-svg="isGetSvg.escolaridade"
          @svg="(value) => svg.escolaridade = value"
        />
      </v-col>

      <!-- Quadro Resumo - níveis de escolaridade -->
      <v-col cols="12">
        <app-title texto="QUADRO RESUMO DE ESCOLARIDADE" cor="secondary" />
      </v-col>
      <v-col>
        <v-data-table
          :headers="headersResumoNiveisEscolaridade"
          :items="[dadosResumoNiveisEscolaridade]"
          disable-pagination
          hide-default-footer
          class="elevation-0 mb-4"
        />
      </v-col>

      <v-col cols="12">
        <app-title texto="FORMAÇÃO COMPLEMENTAR" />
      </v-col>

      <!-- Gráfico de barras - idiomas -->
      <v-col cols="12">
        <app-title texto="IDIOMAS" cor="secondary" />
      </v-col>
      <v-col cols="12">
        <bar-chart
          :categorias="dadosBarChartIdiomas.categorias"
          :dados="dadosBarChartIdiomas.dados"
          :fonte="`Fonte: Sistema Desenvolva, ${ano}`"
          :tooltip="tooltipIdiomas"
          class="chart"
          titulo="Comparativo de Idiomas"
          descricao-eixo-y="Quantidade de Servidores"
          exibe-categorias
          :is-get-svg="isGetSvg.idioma"
          @svg="(value) => svg.idioma = value"
        />
      </v-col>

      <!-- Quadro Resumo - idiomas -->
      <v-col cols="12">
        <app-title texto="QUADRO RESUMO DE IDIOMAS" cor="secondary" />
      </v-col>
      <v-col>
        <v-data-table
          :headers="headersResumoIdiomas"
          :items="[dadosResumoIdiomas]"
          disable-pagination
          hide-default-footer
          class="elevation-0 mb-4"
        />
      </v-col>

      <!-- Gráfico de pizza - interesses: instrutor de capacitação -->
      <v-col cols="12">
        <app-title texto="INTERESSES: INSTRUTOR DE CAPACITAÇÃO" cor="secondary" />
      </v-col>
      <v-col cols="12">
        <pie-chart
          titulo="Comparativo de Interesses<br>Instrutor de Capacitação"
          :fonte="`Sistema Desenvolva, ${ano}`"
          :dados="dadosPieChartInstrutorCapacitacao.series"
          :is-get-svg="isGetSvg.capacitacao"
          @svg="(value) => svg.capacitacao = value"
        />
      </v-col>

      <!-- Quadro Resumo - interesses: instrutor de capacitação -->
      <v-col cols="12">
        <app-title texto="QUADRO RESUMO DE INTERESSES: INSTRUTOR DE CAPACITAÇÃO" cor="secondary" />
      </v-col>
      <v-col>
        <v-data-table
          :headers="headersResumoInstrutorCapacitacao"
          :items="[dadosResumoInstrutorCapacitacao]"
          disable-pagination
          hide-default-footer
          class="elevation-0 mb-4"
        />
      </v-col>

      <!-- Resultados -->
      <v-col cols="12">
        <app-title texto="RESULTADOS" cor="secondary" />
      </v-col>
      <v-col cols="12">
        <tabela-resultado
          :relatorio="relatorio"
          idiomas-attr="idiomas"
          is-instrutor-attr="isInstrutorCapacitacao"
          cursos-attr="cursosCapacitacao"
        />
      </v-col>
    </v-row>

    <!-- Relatório PDF -->
    <v-row
      class="justify-end mb-1"
    >
      <v-tooltip
        max-width="400"
        top
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="success"
            @click="getRelatorio()"
            v-on="on"
          >
            <v-icon>get_app</v-icon>
            BAIXAR PDF
          </v-btn>
        </template>
        <span>Clique para baixar o relatório dos dados em formato PDF.</span>
      </v-tooltip>
    </v-row>
  </div>
</template>

<script>
import AppTitle from '@/components/core/AppTitle';
import BarChart from '@/components/chart/BarChart';
import PieChart from '@/components/chart/PieChart';
import { normalizar } from '@/modules/texto';
import { generatePDF, preparePdf, convertSvg } from '@/modules/relatorioPDF';
import TabelaResultado from './TabelaResultadoUnidadeFormacao';

export default {
  name: 'RelatorioPerfilUnidadeFormacao',

  components: {
    AppTitle,
    BarChart,
    PieChart,
    TabelaResultado,
  },

  props: {
    relatorio: {
      type: Object,
      default: () => ({}),
    },
    ano: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      headersResumoNiveisEscolaridade: [
        {
          text: 'UNIDADE',
          value: 'sigla',
          sortable: false,
        },
        ...this.relatorio.niveisEscolaridade.map((d) => ({
          text: d.nome.toUpperCase(),
          value: normalizar(d.nome),
          sortable: false,
          align: 'center',
        })),
        {
          text: 'TOTAL DE SERVIDORES DA UNIDADE',
          value: 'servidores',
          width: '10%',
          sortable: false,
          align: 'center',
        },
      ],
      tooltipIdiomas: {
        formatter() {
          return `
            <span style="padding:2px;color:${this.series.color}"><b>${this.point.name}</b></span>
            <table>
              <tr>
                <td style="padding:0">Quantidade de Servidores: </td>
                <td style="padding:2px;text-align:right"><span>${this.point.y}</span></td>
              </tr>
            </table>
          `;
        },
      },
      headersResumoIdiomas: [
        {
          text: 'UNIDADE',
          value: 'nome',
          sortable: false,
        },
        {
          text: 'UNIDADE',
          value: 'sigla',
          sortable: false,
        },
        ...this.relatorio.idiomas.map((d) => ({
          text: d.nome.toUpperCase(),
          value: normalizar(d.nome),
          sortable: false,
          align: 'center',
        })),
      ],
      headersResumoInstrutorCapacitacao: [
        {
          text: 'UNIDADE',
          value: 'nome',
          sortable: false,
        },
        {
          text: 'SIGLA',
          value: 'sigla',
          sortable: false,
        },
        {
          text: 'INTERESSADOS EM INSTRUTORIA DE CAPACITAÇÃO',
          value: 'quantidadeInstrutorCapacitacao',
          width: '10%',
          sortable: false,
          align: 'center',
        },
        {
          text: 'NÃO INTERESSADOS EM INSTRUTORIA DE CAPACITAÇÃO',
          value: 'quantidadeNaoInstrutorCapacitacao',
          width: '10%',
          sortable: false,
          align: 'center',
        },
        {
          text: 'TOTAL DE SERVIDORES DA UNIDADE',
          value: 'servidores',
          width: '10%',
          sortable: false,
          align: 'center',
        },
      ],
      isGetSvg: {
        escolaridade: false,
        idioma: false,
        capacitacao: false,
      },
      svg: {
        escolaridade: null,
        idioma: null,
        capacitacao: null,
      },
    };
  },

  computed: {
    dadosPieChartNiveisEscolaridade() {
      return {
        series: [
          {
            name: 'Respostas',
            data: [
              ...this.relatorio.niveisEscolaridade.filter((n) => n.quantidade > 0).map((d) => ({
                name: d.nome,
                y: d.quantidade,
              })).sort((a, b) => {
                if (a.y < b.y) { return 1; }
                if (a.y > b.y) { return -1; }
                return 0;
              }),
            ],
          },
        ],
      };
    },
    dadosResumoNiveisEscolaridade() {
      const niveisEscolaridade = {};
      this.relatorio.niveisEscolaridade.forEach((d) => niveisEscolaridade[normalizar(d.nome)] = d.quantidade);

      return {
        nome: this.relatorio.nome,
        sigla: this.relatorio.sigla,
        ...niveisEscolaridade,
        servidores: this.relatorio.quantidadePerfis,
      };
    },
    dadosBarChartIdiomas() {
      const dados = this.relatorio.idiomas.map((d) => ({
        ...d,
        name: d.nome,
        y: d.quantidade,
      })).sort((a, b) => {
        if (a.y < b.y) { return 1; }
        if (a.y > b.y) { return -1; }
        return 0;
      });

      return {
        dados: [{
          name: 'Idioma',
          data: dados,
          color: '#2D5269',
        }],
        categorias: dados.map((a) => a.nome),
      };
    },
    dadosResumoIdiomas() {
      const idiomas = {};
      this.relatorio.idiomas.forEach((d) => idiomas[normalizar(d.nome)] = d.quantidade);

      return {
        nome: this.relatorio.nome,
        sigla: this.relatorio.sigla,
        ...idiomas,
      };
    },
    dadosPieChartInstrutorCapacitacao() {
      return {
        series: [
          {
            name: 'Respostas',
            data: [
              {
                name: 'Sim',
                y: this.relatorio.quantidadeInstrutorCapacitacao,
              },
              {
                name: 'Não',
                y: this.relatorio.quantidadeNaoInstrutorCapacitacao,
              },
              {
                name: 'Não respondeu',
                y: this.relatorio.quantidadeNaoRespondeuInstrutorCapacitacao,
                color: 'grey',
              },
            ],
          },
        ],
      };
    },
    dadosResumoInstrutorCapacitacao() {
      return {
        nome: this.relatorio.nome,
        sigla: this.relatorio.sigla,
        quantidadeInstrutorCapacitacao: this.relatorio.quantidadeInstrutorCapacitacao,
        quantidadeNaoInstrutorCapacitacao: this.relatorio.quantidadeNaoInstrutorCapacitacao,
        servidores: this.relatorio.quantidadePerfis,
      };
    },
  },

  methods: {
    async getRelatorio() {
      this.isGetSvg.escolaridade = true;
      this.isGetSvg.idioma = true;
      this.isGetSvg.capacitacao = true;
      await preparePdf(async () => {
        const documento = {
          nome: 'relatorio-unidade-formacao-perfil',
          orientacao: 'landscape',
          cabecalho: {
            titulo: 'Relatório Por Unidade da Formação do Perfil',
            itens: [
              `Unidade: ${this.relatorio.nome} (${this.relatorio.sigla})`,
            ],
          },
          blocos: [
            {
              titulo: 'GRÁFICOS',
              subtitulo: 'ESCOLARIDADE',
              itens: [
                { tipo: 'imagem', valor: await convertSvg(this.svg.escolaridade) },
              ],
            },
            {
              titulo: 'QUADRO RESUMO',
              subtitulo: 'ESCOLARIDADE',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'UNIDADE',
                    ...this.relatorio.niveisEscolaridade.map((d) => d.nomeAbreviado.split(' ')
                      .join('\n')
                      .toUpperCase()),
                    'TOTAL DE\nSERVIDO-\nRES',
                  ],
                  linhas: [
                    [
                      `${this.relatorio.nome} (${this.relatorio.sigla})`,
                      ...this.relatorio.niveisEscolaridade.map((n) => n.quantidade),
                      this.relatorio.quantidadePerfis,
                    ],
                  ],
                },
              }],
            },
            {
              titulo: 'GRÁFICOS',
              subtitulo: 'IDIOMA',
              itens: [
                { tipo: 'imagem', valor: await convertSvg(this.svg.idioma), largura: 160 },
              ],
            },
            {
              titulo: 'QUADRO RESUMO',
              subtitulo: 'IDIOMA',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'UNIDADE',
                    ...this.relatorio.idiomas.map((i) => i.nome.split(' ')
                      .join('\n')
                      .toUpperCase()),
                  ],
                  linhas: [
                    [
                      `${this.relatorio.nome} (${this.relatorio.sigla})`,
                      ...this.relatorio.idiomas.map((i) => i.quantidade),
                    ],
                  ],
                },
              }],
            },
            {
              titulo: 'GRÁFICOS',
              subtitulo: 'INSTRUTOR DE CAPACITAÇÃO',
              itens: [
                { tipo: 'imagem', valor: await convertSvg(this.svg.capacitacao) },
              ],
            },
            {
              titulo: 'QUADRO RESUMO',
              subtitulo: 'INSTRUTOR DE CAPACITAÇÃO',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'UNIDADE',
                    'INTERESSADOS',
                    'NÃO\nINTERESSADOS',
                    'NÃO\nRESPONDEU',
                    'TOTAL DE\nSERVIDORES',
                    'CURSOS\nCADASTRADOS',
                  ],
                  linhas: [
                    [
                      `${this.relatorio.nome} (${this.relatorio.sigla})`,
                      this.relatorio.quantidadeInstrutorCapacitacao,
                      this.relatorio.quantidadeNaoInstrutorCapacitacao,
                      this.relatorio.quantidadeNaoRespondeuInstrutorCapacitacao,
                      this.relatorio.quantidadePerfis,
                      this.relatorio.cursosCapacitacao.length,
                    ],
                  ],
                },
              }],
            },
            {
              titulo: 'RESULTADOS',
              subtitulo: 'INSTRUTOR DE CAPACITAÇÃO',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'SERVIDOR',
                    'NÍVEL DE\nESCOLARIDADE',
                    'IDIOMAS',
                    'INSTRUTOR\nDE\nCAPACITAÇÃO',
                    'CURSOS\nCADASTRADOS',
                  ],
                  linhas: this.relatorio.perfis.map((p) => ([
                    p.servidor,
                    p.nivelEscolaridade ? p.nivelEscolaridade.nome : '',
                    p.idiomas.map((i) => i.nome).join(', '),
                    this.getIsInstrutorCapacitacao(p.isInstrutorCapacitacao),
                    p.cursosCapacitacao.length,
                  ])),
                },
              }],
            },
          ],
        };
        generatePDF(documento);
      });
      this.isGetSvg.escolaridade = false;
      this.isGetSvg.idioma = false;
      this.isGetSvg.capacitacao = false;
    },
    getIsInstrutorCapacitacao(isInstrutorCapacitacao) {
      if (isInstrutorCapacitacao) {
        return 'Sim';
      }
      if (isInstrutorCapacitacao === null) {
        return 'Não Respondeu';
      }
      return 'Não';
    },
  },
};
</script>

<style scoped>
.chart {
  width: 100%;
  max-height: 400px;
  font-family: Roboto, sans-serif;
}

.chart >>> .highcharts-legend-item rect {
  display: none;
}

.chart >>> .highcharts-legend-item {
  pointer-events: none;
}
</style>
